import { useEffect, useMemo, useState, useCallback, Fragment } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Download,
  RefreshCcw,
  Check,
  Trash2,
  Search,
  Mail,
  Phone,
  Shield,
  Inbox,
  CircleCheckBig,
  ListFilter,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "avvanta_admin_token";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

/* ---------------- LOGIN ---------------- */
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const token = res.data?.token;
      if (!token) throw new Error("Missing token");
      localStorage.setItem(STORAGE_KEY, token);
      onLogin(token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="page-offset min-h-screen bg-[#F8F9FA] flex items-center py-16">
      <div className="w-full max-w-md mx-auto px-6">
        <div className="bg-white border border-gray-200 p-10 shadow-[0_30px_80px_-40px_rgba(10,31,68,0.25)]">
          <div className="flex items-center gap-3 text-[#D4AF37]">
            <Shield size={22} strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.32em] font-semibold">
              Admin Access
            </span>
          </div>
          <h1 className="mt-5 font-display text-3xl md:text-4xl text-[#0A1F44] leading-tight">
            Avvanta Advisory{" "}
            <span className="italic font-light">Console</span>
          </h1>
          <div className="mt-4 h-[2px] w-10 bg-[#D4AF37]" />
          <p className="mt-5 text-[14px] text-[#4B5563]">
            Enter the admin password to view &amp; manage contact submissions.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.22em] text-[#0A1F44] font-semibold mb-2">
                Password
              </label>
              <input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full border border-gray-300 px-4 py-3 text-[15px] bg-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#0A1F44] text-white px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-colors disabled:opacity-70"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

/* ---------------- DASHBOARD ---------------- */
function AdminDashboard({ token, onLogout }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, handled: 0 });
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | handled
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, {
          params: { status: statusFilter },
          headers: authHeader,
        }),
        axios.get(`${API}/admin/contacts/stats`, { headers: authHeader }),
      ]);
      setItems(listRes.data || []);
      setStats(statsRes.data || { total: 0, pending: 0, handled: 0 });
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, statusFilter, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.name, it.email, it.phone, it.service, it.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  const toggleHandled = async (row) => {
    try {
      const res = await axios.patch(
        `${API}/admin/contacts/${row.id}`,
        { handled: !row.handled },
        { headers: authHeader }
      );
      setItems((prev) => prev.map((r) => (r.id === row.id ? res.data : r)));
      setStats((s) => ({
        ...s,
        pending: s.pending + (res.data.handled ? -1 : 1),
        handled: s.handled + (res.data.handled ? 1 : -1),
      }));
      toast.success(res.data.handled ? "Marked as handled." : "Marked as pending.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async (row, notes) => {
    try {
      const res = await axios.patch(
        `${API}/admin/contacts/${row.id}`,
        { notes },
        { headers: authHeader }
      );
      setItems((prev) => prev.map((r) => (r.id === row.id ? res.data : r)));
      toast.success("Notes saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete submission from ${row.name}? This cannot be undone.`))
      return;
    try {
      await axios.delete(`${API}/admin/contacts/${row.id}`, { headers: authHeader });
      setItems((prev) => prev.filter((r) => r.id !== row.id));
      setStats((s) => ({
        total: s.total - 1,
        pending: s.pending - (row.handled ? 0 : 1),
        handled: s.handled - (row.handled ? 1 : 0),
      }));
      toast.success("Deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts/export.csv`, {
        headers: authHeader,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `avvanta-contacts-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <section className="page-offset min-h-screen bg-[#F8F9FA]">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 text-[#D4AF37]">
              <Shield size={18} strokeWidth={1.75} />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold">
                Admin Console
              </span>
            </div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl text-[#0A1F44]">
              Contact Submissions
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-gray-300 text-[#0A1F44] px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <RefreshCcw size={14} /> Refresh
            </button>
            <button
              type="button"
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 bg-[#0A1F44] text-white px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[#4B5563] px-3 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] hover:text-[#0A1F44] transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { label: "Total", value: stats.total, icon: Inbox, accent: "#0A1F44" },
            { label: "Pending", value: stats.pending, icon: ListFilter, accent: "#D4AF37" },
            { label: "Handled", value: stats.handled, icon: CircleCheckBig, accent: "#10B981" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                data-testid={`admin-stat-${s.label.toLowerCase()}`}
                className="relative bg-white border border-gray-200 p-6 overflow-hidden"
              >
                <span
                  className="absolute top-0 left-0 h-full w-[3px]"
                  style={{ background: s.accent }}
                />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#4B5563] font-semibold">
                      {s.label}
                    </div>
                    <div className="mt-3 font-display text-4xl text-[#0A1F44] font-medium tracking-tight leading-none">
                      {s.value}
                    </div>
                  </div>
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F8F9FA] text-[#0A1F44]">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-gray-200 p-4 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="inline-flex items-center border border-gray-200">
            {[
              { k: "all", l: "All" },
              { k: "pending", l: "Pending" },
              { k: "handled", l: "Handled" },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setStatusFilter(t.k)}
                data-testid={`admin-filter-${t.k}`}
                className={`px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                  statusFilter === t.k
                    ? "bg-[#0A1F44] text-white"
                    : "bg-white text-[#0A1F44] hover:bg-[#F8F9FA]"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
          <div className="relative flex-1 md:max-w-md">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]"
              strokeWidth={1.75}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
              placeholder="Search name, email, message..."
              className="w-full border border-gray-300 pl-10 pr-4 py-2.5 text-[14px] bg-white focus:outline-none focus:border-[#D4AF37] transition-colors"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-contacts-table">
              <thead>
                <tr className="bg-[#F8F9FA] text-left text-[11px] uppercase tracking-[0.2em] text-[#0A1F44] font-semibold">
                  <th className="px-5 py-4 whitespace-nowrap">Received</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Message</th>
                  <th className="px-5 py-4 whitespace-nowrap">Status</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-[#4B5563]">
                      Loading submissions...
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center text-[#4B5563]">
                      <Inbox size={28} strokeWidth={1.25} className="mx-auto mb-3 text-[#9CA3AF]" />
                      No submissions match this view.
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((row) => {
                    const open = expandedId === row.id;
                    return (
                      <Fragment key={row.id}>
                        <tr
                          data-testid={`admin-row-${row.id}`}
                          className="border-t border-gray-200 hover:bg-[#F8F9FA]/60 transition-colors"
                        >
                          <td className="px-5 py-5 text-[13px] text-[#4B5563] whitespace-nowrap">
                            {formatDate(row.created_at)}
                          </td>
                          <td className="px-5 py-5">
                            <div className="font-display text-[15px] text-[#0A1F44] font-medium">
                              {row.name}
                            </div>
                            <div className="mt-1 flex flex-col gap-0.5 text-[12px] text-[#4B5563]">
                              <a
                                href={`mailto:${row.email}`}
                                className="inline-flex items-center gap-1.5 hover:text-[#D4AF37]"
                              >
                                <Mail size={11} /> {row.email}
                              </a>
                              <a
                                href={`tel:${row.phone}`}
                                className="inline-flex items-center gap-1.5 hover:text-[#D4AF37]"
                              >
                                <Phone size={11} /> {row.phone}
                              </a>
                            </div>
                          </td>
                          <td className="px-5 py-5 text-[13px] text-[#111827]">
                            {row.service || <span className="text-[#9CA3AF]">—</span>}
                          </td>
                          <td className="px-5 py-5 text-[13px] text-[#111827] max-w-md">
                            <button
                              type="button"
                              onClick={() => setExpandedId(open ? null : row.id)}
                              className="text-left hover:text-[#D4AF37] transition-colors line-clamp-2"
                              title={row.message}
                            >
                              {row.message}
                            </button>
                          </td>
                          <td className="px-5 py-5 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold ${
                                row.handled
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}
                            >
                              {row.handled ? "Handled" : "Pending"}
                            </span>
                          </td>
                          <td className="px-5 py-5 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleHandled(row)}
                                data-testid={`admin-toggle-${row.id}`}
                                className="inline-flex items-center gap-1.5 border border-gray-300 px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-[#0A1F44] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                              >
                                <Check size={13} />
                                {row.handled ? "Reopen" : "Mark Done"}
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteRow(row)}
                                data-testid={`admin-delete-${row.id}`}
                                className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 px-3 py-2 text-[11px] uppercase tracking-[0.14em] hover:bg-red-50 transition-colors"
                              >
                                <Trash2 size={13} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-[#F8F9FA]/60">
                            <td colSpan={6} className="px-5 pb-6 pt-0">
                              <div className="border-t border-gray-200 pt-4 grid grid-cols-1 lg:grid-cols-3 gap-5">
                                <div className="lg:col-span-2 bg-white border border-gray-200 p-5">
                                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] font-semibold">
                                    Full Message
                                  </div>
                                  <p className="mt-3 text-[14px] text-[#111827] leading-relaxed whitespace-pre-wrap">
                                    {row.message}
                                  </p>
                                </div>
                                <NotesEditor row={row} onSave={saveNotes} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function NotesEditor({ row, onSave }) {
  const [value, setValue] = useState(row.notes || "");
  useEffect(() => setValue(row.notes || ""), [row.id, row.notes]);
  const dirty = value !== (row.notes || "");
  return (
    <div className="bg-white border border-gray-200 p-5">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] font-semibold">
        Internal Notes
      </div>
      <textarea
        rows={5}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add private notes — next steps, owner, status..."
        className="mt-3 w-full border border-gray-300 px-3 py-2.5 text-[13px] bg-white focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
      />
      <button
        type="button"
        disabled={!dirty}
        onClick={() => onSave(row, value)}
        data-testid={`admin-save-notes-${row.id}`}
        className="mt-3 inline-flex items-center gap-2 bg-[#0A1F44] text-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Save Notes
      </button>
    </div>
  );
}

/* ---------------- ROOT ---------------- */
export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
  };

  if (!token) return <AdminLogin onLogin={setToken} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
